import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { UpsertPromptRequest } from "@/api/models"

import { Building2, HelpCircle, Settings, Shield, User } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { TrustedSourcesDialog } from "@/features/trusted-sources/components/trusted-sources-dialog"
import { useGetTrustedSources } from "@/features/trusted-sources/hooks/use-get-trusted-sources"

type PromptWebSearchSettingsProps = {
  updateData: Partial<UpsertPromptRequest>
  onUpdate: (data: Partial<UpsertPromptRequest>) => void
}

export function PromptWebSearchSettings({ updateData, onUpdate }: PromptWebSearchSettingsProps) {
  const { t } = useTranslation("", { keyPrefix: "prompt_settings.web_search" })

  const { data: sourcesData, isPending: isSourcesPending } = useGetTrustedSources()
  const allSources = sourcesData?.sources || []
  const selectedUrls = new Set(updateData.trustedSourceUrls || [])

  const toggleTrustedUrl = (url: string, checked: boolean) => {
    const urls = new Set(updateData.trustedSourceUrls || [])
    if (checked) {
      urls.add(url)
    } else {
      urls.delete(url)
    }
    onUpdate({ trustedSourceUrls: Array.from(urls) })
  }

  const [trustedSourcesDialogOpen, setTrustedSourcesDialogOpen] = useState(false)

  const isWebSearchEnabled = !!updateData.defaultWebSearch
  const useTrustedSources = !!updateData.useTrustedSources

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium" htmlFor="default-web-search">
                {t("enable_web_search")}
              </Label>
              <p className="text-muted-foreground text-xs">{t("enable_web_search_description")}</p>
            </div>
            <Switch
              checked={isWebSearchEnabled}
              id="default-web-search"
              onCheckedChange={(v) => onUpdate({ defaultWebSearch: !!v })}
            />
          </div>
        </div>

        {isWebSearchEnabled && (
          <>
            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium" htmlFor="use-trusted-sources">
                    {t("use_trusted_sources")}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("use_trusted_sources_description")}
                  </p>
                </div>
                <Switch
                  checked={useTrustedSources}
                  id="use-trusted-sources"
                  onCheckedChange={(v) => onUpdate({ useTrustedSources: !!v })}
                />
              </div>
            </div>
          </>
        )}

        {isWebSearchEnabled && useTrustedSources && (
          <>
            <Separator />

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium">{t("trusted_sources_title")}</h4>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="text-muted-foreground h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px]" side="right">
                    <p>{t("trusted_sources_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Mode Selection */}
              <RadioGroup
                value={updateData.trustedMode || "only"}
                onValueChange={(v) => onUpdate({ trustedMode: v as "only" | "preferred" })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem id="mode-only" value="only" />
                  <Label className="text-sm font-normal" htmlFor="mode-only">
                    {t("mode_only")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem id="mode-preferred" value="preferred" />
                  <Label className="text-sm font-normal" htmlFor="mode-preferred">
                    {t("mode_preferred")}
                  </Label>
                </div>
              </RadioGroup>

              <Separator className="my-3" />

              {isSourcesPending && (
                <div className="flex items-center gap-2 py-4">
                  <Spinner className="h-4 w-4" />
                  <span className="text-muted-foreground text-sm">{t("loading_sources")}</span>
                </div>
              )}

              {!isSourcesPending && allSources.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Shield className="text-muted-foreground/50 mb-2 h-8 w-8" />
                  <p className="text-muted-foreground text-sm">{t("no_sources")}</p>
                </div>
              )}

              {!isSourcesPending && allSources.length > 0 && (
                <div className="space-y-2">
                  {allSources.map((source) => (
                    <div
                      key={source.id}
                      className="bg-card hover:bg-accent/50 flex items-center justify-between rounded-lg border p-3 transition-colors"
                    >
                      <div className="grid min-w-0 flex-1 gap-1.5 leading-none">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium" htmlFor={`source-${source.id}`}>
                            {source.name}
                          </Label>
                          {source.type === "company" ? (
                            <Badge
                              className="h-5 gap-1 text-xs dark:text-white"
                              variant="secondary"
                            >
                              <Building2 className="h-3 w-3" />
                              Company
                            </Badge>
                          ) : (
                            <Badge className="h-5 gap-1 text-xs" variant="outline">
                              <User className="h-3 w-3" />
                              Personal
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-xs break-all">{source.url}</p>
                      </div>
                      <Switch
                        checked={selectedUrls.has(source.url)}
                        id={`source-${source.id}`}
                        onCheckedChange={(v) => toggleTrustedUrl(source.url, !!v)}
                      />
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full gap-2"
                variant="outline"
                onClick={() => setTrustedSourcesDialogOpen(true)}
              >
                <Settings className="h-4 w-4" />
                {t("manage_sources")}
              </Button>
            </div>
          </>
        )}
      </div>

      <TrustedSourcesDialog
        open={trustedSourcesDialogOpen}
        onOpenChange={setTrustedSourcesDialogOpen}
      />
    </>
  )
}
